// Spreadsheet reading for contact import. CSV is parsed here; Excel files load SheetJS on demand.

import type { CellObject } from 'xlsx';
import { firstEmail, isValidEmail, suggestFix } from '../../shared/emailAddress';
import type { ContactStatus, ImportRow } from '../../shared/types';

const DELIMS = [',', ';', '\t'];

/** RFC 4180-ish: quoted fields, doubled quotes, commas/semicolons/tabs, CRLF, BOM. */
export function parseCsv(text: string): string[][] {
  const s = text.replace(/^﻿/, '');
  return parseWith(s, pickDelimiter(s));
}

/** The delimiter that splits the first records (outside quotes) into the most consistent number of columns. */
function pickDelimiter(s: string): string {
  const cut = s.length > 64_000;
  let best = ',';
  let bestScore = 0;
  for (const d of DELIMS) {
    let rows = parseWith(cut ? s.slice(0, 64_000) : s, d).filter((r) => r.some((c) => c.trim() !== ''));
    if (cut) rows = rows.slice(0, -1); // the last record may be cut in half
    rows = rows.slice(0, 20);
    const counts = new Map<number, number>();
    for (const r of rows) counts.set(r.length, (counts.get(r.length) || 0) + 1);
    const [cols, hits] = [...counts].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0] || [1, 0];
    if (cols < 2) continue;
    const score = (hits / rows.length) * 1000 + cols;
    if (score > bestScore) [best, bestScore] = [d, score];
  }
  return best;
}

function parseWith(s: string, delim: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let q = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else q = false;
      } else field += c;
    } else if (c === '"' && field === '') q = true;
    else if (c === delim) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** Drops empty rows and blank rows above the header. */
export function tidy(rows: string[][]): string[][] {
  const out = rows.map((r) => r.map((c) => (c ?? '').toString().trim())).filter((r) => r.some((c) => c !== ''));
  return out;
}

export async function readSheet(file: File): Promise<string[][]> {
  if (/\.(xlsx|xls|xlsm|ods)$/i.test(file.name)) {
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true, cellNF: true });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    if (!sheet?.['!ref']) return [];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const rows: string[][] = [];
    for (let r = range.s.r; r <= range.e.r; r++) {
      const row: string[] = [];
      for (let c = range.s.c; c <= range.e.c; c++) row.push(cellText(sheet[XLSX.utils.encode_cell({ r, c })] as CellObject | undefined));
      rows.push(row);
    }
    return tidy(rows);
  }
  return tidy(parseCsv(await file.text()));
}

/** What the cell shows in Excel: dates as YYYY-MM-DD, and formatted numbers (a phone with leading zeros) as displayed. */
function cellText(c: CellObject | undefined): string {
  if (!c || c.v == null) return '';
  if (c.v instanceof Date) return c.v.toISOString().slice(0, 10);
  // "General" numbers keep their full value; Excel would show a long phone as 4.47911E+11.
  if (c.t === 'n' && c.w && c.z && c.z !== 'General') return c.w;
  return String(c.v);
}

export type ColumnKind = 'email' | 'first_name' | 'last_name' | 'full_name' | 'phone' | 'last_visit_at' | 'tags' | 'status' | 'skip';

export const COLUMN_LABELS: Record<ColumnKind, string> = {
  email: 'Email',
  first_name: 'First name',
  last_name: 'Last name',
  full_name: 'Full name',
  phone: 'Phone',
  last_visit_at: 'Last visit',
  tags: 'Tags',
  status: 'Unsubscribed or not',
  skip: "Don't import",
};

// Header words that mean "yes = do not email" and "yes = ok to email".
const OPT_OUT_HEADER = /(unsub|optout|optedout|donotemail|donotcontact|donotmarket|noemail|nomarketing|suppress|blocked|dnc)/;
const OPT_IN_HEADER = /(subscri|optin|optedin|emailconsent|marketingconsent|emailmarketing|marketingemail|acceptsmarketing|acceptsemail|allowemail|emailallowed|emailpermission|emailok)/;
// Opt-outs for texts, calls or photos say nothing about email.
const NOT_EMAIL_HEADER = /(sms|text|call|phone|mobile|cell|whatsapp|photo)/;

/** Guess what each column holds from its header (and the data, for email). */
export function guessColumns(header: string[], sample: string[][]): ColumnKind[] {
  const used = new Set<ColumnKind>();
  const out = header.map((h, i): ColumnKind => {
    const n = h.toLowerCase().replace(/[^a-z]/g, '');
    const pick = (k: ColumnKind): ColumnKind => (used.has(k) ? 'skip' : (used.add(k), k));
    // Before email, because "Email Opt Out" and "Email Status" also contain "email".
    if (!NOT_EMAIL_HEADER.test(n) && (/^(status|emailstatus|contactstatus|subscriptionstatus)$/.test(n) || OPT_OUT_HEADER.test(n) || OPT_IN_HEADER.test(n))) return pick('status');
    if (/^(email|emailaddress|e?mail|primaryemail|emailid)$/.test(n) || n.includes('email')) return pick('email');
    if (/^(first|firstname|fname|givenname|first_name)$/.test(n) || n === 'patientfirstname') return pick('first_name');
    if (/^(last|lastname|lname|surname|familyname)$/.test(n) || n === 'patientlastname') return pick('last_name');
    if (/^(name|fullname|patient|patientname|client|clientname|customer|customername)$/.test(n)) return pick('full_name');
    if (n.includes('phone') || n.includes('mobile') || n.includes('cell')) return pick('phone');
    if (/(lastvisit|lastappointment|lastappt|lastservice|lastseen|lastcheckin)/.test(n)) return pick('last_visit_at');
    if (/^(tags?|groups?|labels?|category)$/.test(n)) return pick('tags');
    // No header match: a column full of addresses is the email column.
    const vals = sample.map((r) => r[i] || '').filter(Boolean);
    if (vals.length && vals.filter((v) => /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(v)).length / vals.length > 0.6) return pick('email');
    return 'skip';
  });
  return out;
}

/**
 * Reads a status or opt-out cell. Returns the status that stops emails, or '' when the cell does not say to stop.
 * Yes/no answers depend on the header: "Opt Out: yes" and "Subscribed: no" both mean unsubscribed.
 */
export function toStatus(value: string, header: string): Exclude<ContactStatus, 'active'> | '' {
  const v = value.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!v) return '';
  if (/(complain|spam|abuse)/.test(v)) return 'complained';
  if (/(bounce|undeliverable|invalid)/.test(v)) return 'bounced';
  if (/(unsub|optout|optedout|donot|noemail|nomarketing|suppress|blocked|dnc)/.test(v)) return 'unsubscribed';
  const yes = /^(y|yes|true|1|x|checked|on)$/.test(v);
  const no = /^(n|no|false|0|unchecked|off)$/.test(v);
  const h = header.toLowerCase().replace(/[^a-z]/g, '');
  if (OPT_OUT_HEADER.test(h)) return yes ? 'unsubscribed' : '';
  if (OPT_IN_HEADER.test(h)) return no ? 'unsubscribed' : '';
  return '';
}

/** Does the first row look like headers (no email addresses in it)? */
export function hasHeader(rows: string[][]): boolean {
  return !!rows[0] && !rows[0].some((c) => /@/.test(c));
}

/** Dates: 2025-11-04, 11/4/2025, 11/4/25, or an Excel serial number. Returns YYYY-MM-DD or ''. */
export function toDate(v: string): string {
  const s = v.trim();
  if (!s) return '';
  if (/^\d{5}(\.\d+)?$/.test(s)) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(s)) * 86_400_000);
    return d.toISOString().slice(0, 10);
  }
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/.exec(s);
  if (m) {
    const y = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${y}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

export interface Prepared {
  rows: ImportRow[];
  invalid: { line: number; email: string }[];
  typos: { line: number; email: string; fix: string }[];
  dupes: number;
  /** People the file marks as unsubscribed, bounced or spam. They are imported with emails off. */
  optedOut: number;
}

const STATUS_RANK: Record<ContactStatus, number> = { active: 0, unsubscribed: 1, bounced: 2, complained: 3 };

/** Our own export (and most apps) put ' in front of a cell starting with = + - @ so spreadsheets don't run it. Undo that. */
function unescapeCell(s: string): string {
  return s.replace(/^'(?=[=+\-@])/, '');
}

/** Turn mapped rows into import rows. Merges repeats; collects bad addresses and likely typos. */
export function prepareRows(header: string[], rows: string[][], cols: ColumnKind[], fixes: Record<number, boolean>): Prepared {
  const col = (k: ColumnKind) => cols.indexOf(k);
  const iEmail = col('email');
  const out = new Map<string, ImportRow>();
  const invalid: Prepared['invalid'] = [];
  const typos: Prepared['typos'] = [];
  let dupes = 0;
  if (iEmail < 0) return { rows: [], invalid, typos, dupes, optedOut: 0 };
  const get = (r: string[], k: ColumnKind) => {
    const i = col(k);
    return i < 0 ? '' : unescapeCell((r[i] || '').trim());
  };
  rows.forEach((r, n) => {
    const line = n + 2;
    // Some exports put two addresses in one cell. Use the first.
    let email = firstEmail(get(r, 'email'));
    const fix = email ? suggestFix(email) : null;
    if (fix) {
      typos.push({ line, email, fix });
      if (fixes[line] !== false) email = fix;
    }
    if (!isValidEmail(email)) {
      if (email || r.some(Boolean)) invalid.push({ line, email });
      return;
    }
    let first = get(r, 'first_name');
    let last = get(r, 'last_name');
    const full = get(r, 'full_name');
    if (full && !first && !last) {
      // "Smith, Ann" or "Ann Smith"
      const parts = full.includes(',') ? full.split(',').map((s) => s.trim()).reverse() : full.split(/\s+/);
      first = parts[0] || '';
      last = parts.slice(1).join(' ');
    }
    const row: ImportRow = { email };
    if (first) row.first_name = tidyName(first);
    if (last) row.last_name = tidyName(last);
    const phone = get(r, 'phone');
    if (phone) row.phone = phone;
    const visit = toDate(get(r, 'last_visit_at'));
    if (visit) row.last_visit_at = visit;
    const tags = get(r, 'tags');
    if (tags) row.tags = tags.split(/[,;|]/).map((t) => t.trim().toLowerCase()).filter(Boolean);
    const iStatus = col('status');
    const status = iStatus < 0 ? '' : toStatus(r[iStatus] || '', header[iStatus] || '');
    if (status) row.status = status;
    const prev = out.get(email);
    if (prev) {
      dupes++;
      // Keep any names the earlier row had, the newest visit, every tag, and the strongest "stop emailing".
      const merged: ImportRow = { ...row, ...Object.fromEntries(Object.entries(prev).filter(([, v]) => v)) };
      merged.last_visit_at = [prev.last_visit_at, row.last_visit_at].filter(Boolean).sort().pop();
      const allTags = [...new Set([...(prev.tags || []), ...(row.tags || [])])];
      if (allTags.length) merged.tags = allTags;
      const st = [prev.status, row.status].filter((s): s is ContactStatus => !!s).sort((a, b) => STATUS_RANK[b] - STATUS_RANK[a])[0];
      if (st) merged.status = st;
      out.set(email, merged);
    } else out.set(email, row);
  });
  const list = [...out.values()];
  return { rows: list, invalid, typos, dupes, optedOut: list.filter((r) => r.status && r.status !== 'active').length };
}

/** "ANN" or "ann" becomes "Ann". Mixed case is left alone. */
function tidyName(s: string): string {
  if (s !== s.toUpperCase() && s !== s.toLowerCase()) return s;
  return s.toLowerCase().replace(/(^|[\s'-])\p{L}/gu, (m) => m.toUpperCase());
}
